import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Eye, Download, IdCard, Pencil, Ban, KeyRound, PackagePlus, FileText } from "lucide-react";
import { Users, TrendingUp, Target } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import { Panigation } from "@/components/ui/Panigation";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import Spinner from "@/components/Spinner";
import { StatsCard } from "@/components/Dashboard/StatsCard";
import { fetchPackageSummary, fetchUserPackages } from "@/api/Admin/userPackage";
import dayjs from "dayjs";

type Investor = {
  id: string;
  code: string;
  name: string;
  avatar?: string;
  email: string;
  phone: string;
  identifier: string;
  segment: "VIP" | "LOYAL" | "REGULAR" | "NEW"; // phân loại theo backend sau
  totalSpend: number;
  totalOrders: number;
  joinedDate: string;
  status?: "Active" | "Inactive";
};

const InvestorManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const { t } = useTranslation();

  const [summaryData, setSummaryData] = useState({
    totalUser: {
      total: 0,
      newThisMonth: 0,
    },
    totalInvestors: {
      total: 0,
      percentageOfTotal: 0,
    },
    totalInvestment: {
      value: 0,
      vsLastMonth: 0,
    },
    totalPay: {
      value: 0,
      vsLastQuarter: 0,
    },
  });
  const [investorData, setInvestorData] = useState<Investor[]>([]);
  const [loading, setLoading] = useState(false);
  const itemsPerPage = 10;

  const  fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await fetchPackageSummary();
      if (res?.data) {
        setSummaryData({
          totalUser: {
            total: res.data.total_investors.value,
            newThisMonth: res.data.total_investors.trend_percentage,
          },
          totalInvestors: {
            total: res.data.total_active_investors.value,
            percentageOfTotal: res.data.total_active_investors.trend_percentage,
          },
          totalInvestment: {
            value: res.data.total_capital_raised.value,
            vsLastMonth: res.data.total_capital_raised.trend_percentage,
          },
          totalPay: {
            value: res.data.total_interest_paid.value,
            vsLastQuarter: res.data.total_interest_paid.trend_percentage,
          },
        });
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
    } finally {
      setLoading(false);
    }
  };


  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetchUserPackages( currentPage, statusFilter);
      if (res?.data?.data) {
        const data: Investor[] = res.data.data.map((item: any) => ({
          id: item.user_id.toString(),
          code: item.user_info.id.toString() || "",
          name: item.user_info.fullname || "",
          email: item.user_info.email || "",
          phone: item.user_info.phone || "",
          identifier: item.user_info.cccd_number || "",
          segment: "REGULAR",
          totalSpend: Number(item.total_invested) || 0,
          totalOrders: item.total_packages || 0,
          joinedDate: item.user_info.created_at || "",
          status: item.user_info.is_active === 1 ? "Active" : "Inactive",
        }));
        setInvestorData(data);
      }
    } catch (error) {
      console.error("Error fetching investors:", error);
    } finally {
      setLoading(false);
    }
  };

  // Mock data
  useEffect(() => {
    fetchData();
  }, [currentPage, searchTerm, statusFilter]);

  useEffect(() => {
    fetchSummary();
  }, []);

  const filteredData = investorData.filter((investor) => {
    const matchesSearch =
      investor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      investor.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      investor.identifier.toLowerCase().includes(searchTerm.toLowerCase());
    // const matchesSegment = !segmentFilter || investor.segment === segmentFilter;
    const matchStatus = !statusFilter || investor.status === statusFilter;
    return matchesSearch && matchStatus;
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const navigate = useNavigate();

  const openInvestorDetails = (investorId: string) => {
    navigate(`/admin/investors/${investorId}`);
  };

  const handleExport = () => {
    console.log("Export data");
  };

  // const getSegmentBadge = (segment: Investor["segment"]) => {
  //   const badges = {
  //     VIP: "bg-yellow-500 text-white dark:bg-yellow-900/30 dark:text-yellow-400",
  //     LOYAL: "bg-blue-500 text-white dark:bg-blue-900/30 dark:text-blue-400",
  //     REGULAR:
  //       "bg-green-500 text-white dark:bg-green-900/30 dark:text-green-400",
  //     NEW: "bg-cyan-500 text-white dark:bg-cyan-900/30 dark:text-cyan-400",
  //   };
  //   const labels = {
  //     VIP: "VIP",
  //     LOYAL: t("investors.loyal"),
  //     REGULAR: t("investors.regular"),
  //     NEW: t("investors.new"),
  //   };
  //   return (
  //     <span
  //       className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${badges[segment]}`}
  //     >
  //       {labels[segment]}
  //     </span>
  //   );
  // };

  const getStatusBadge = (status: Investor["status"]) => {
    if (status === "Active") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-500 text-white dark:bg-green-900/30 dark:text-green-400">
          {t("investors.active")}
        </span>
      );
    } else if (status === "Inactive") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-red-500 text-white dark:bg-red-900/30 dark:text-red-400">
          {t("investors.inactive")}
        </span>
      );
    }
    return null;
  }

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Dashboard", path: "/admin/dashboard" },
    { label: t("investors.title") },
  ];

  // Stats configuration
  const statsConfig = [
    {
      title: t("investors.totalInvestors"),
      value: summaryData.totalUser.total.toLocaleString('vi-VN'),
      change: summaryData.totalUser.newThisMonth.toLocaleString('vi-VN') + "%",
      changeLabel: t("investors.newThisMonth"),
      trend: "up" as const,
      icon: Users,
      bgColor: "bg-blue-500/10 dark:bg-blue-500/20",
      iconColor: "text-blue-600 dark:text-blue-400",
      chartData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      chartColor: "#3b82f6",
      gradientColor: "#3b82f6",
    },
    {
      title: t("investors.vipInvestors"),
      value: summaryData.totalInvestors.total.toLocaleString('vi-VN'),
      change: summaryData.totalInvestors.percentageOfTotal.toLocaleString('vi-VN') + "%",
      changeLabel: t("investors.ofTotal"),
      trend: "neutral" as const,
      icon: TrendingUp,
      bgColor: "bg-yellow-500/10 dark:bg-yellow-500/20",
      iconColor: "text-yellow-600 dark:text-yellow-400",
      chartData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      chartColor: "#eab308",
      gradientColor: "#eab308",
    },
    {
      title: t("investors.avgValue"),
      value: summaryData.totalInvestment.value.toLocaleString('vi-VN') + " đ",
      change: summaryData.totalInvestment.vsLastMonth.toLocaleString('vi-VN') + "%",
      changeLabel: t("investors.vsLastMonth"),
      trend: "up" as const,
      icon: PackagePlus,
      bgColor: "bg-green-500/10 dark:bg-green-500/20",
      iconColor: "text-green-600 dark:text-green-400",
      chartData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      chartColor: "#22c55e",
      gradientColor: "#22c55e",
    },
    {
      title: t("investors.retentionRate"),
      value: summaryData.totalPay.value.toLocaleString('vi-VN') + "%",
      change: summaryData.totalPay.vsLastQuarter.toLocaleString('vi-VN') + "%",
      changeLabel: t("investors.vsLastQuarter"),
      trend: "up" as const,
      icon: Target,
      bgColor: "bg-purple-500/10 dark:bg-purple-500/20",
      iconColor: "text-purple-600 dark:text-purple-400",
      chartData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      chartColor: "#a855f7",
      gradientColor: "#a855f7",
    },
  ];

  return (
    <div className="dark:bg-dark-bg">
      <Helmet>
        <title>{t("investors.title")} - Admin Dashboard</title>
      </Helmet>

      <PageHeader
        title={t("investors.title")}
        description={t("investors.subtitle")}
        breadcrumbs={breadcrumbs}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {statsConfig.map((stat, index) => (
          <StatsCard // backend trả về trường nào thì qua StatsCard hard code trường còn lại
            key={index}
            index={index}
            title={stat.title}
            value={stat.value}
            change={stat.change}
            changeLabel={stat.changeLabel}
            trend={stat.trend}
            icon={stat.icon}
            bgColor={stat.bgColor}
            iconColor={stat.iconColor}
            chartData={stat.chartData}
            chartColor={stat.chartColor}
            gradientColor={stat.gradientColor}
          />
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex md:items-center items-start md:flex-row flex-col gap-3 mb-6">
        <div className="relative flex-1 w-full md:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t("investors.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2.5 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex justify-between items-center gap-2">
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pl-4 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t("investors.allStatuses")}</option>
            <option value="active">{t("investors.active")}</option>
            <option value="inactive">{t("investors.inactive")}</option>
          </select>
        </div>
        <button
          onClick={() => navigate('create-contract')}
          className="flex items-center justify-center px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors font-medium text-sm shadow-sm"
        >
          <FileText className="h-4 w-4 mr-2" />
          {t('plan.createContract')}
        </button>
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <Download className="w-4 h-4" />
          {t("investors.export")}
        </button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.code")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.investor")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.identifier")}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.joinDate")}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.totalSpend")}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.orders")}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.status")}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center">
                    <span className="flex items-center justify-center gap-2">
                      <Spinner size={32} /> {t("common.loading")}...
                    </span>
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-6 py-4 text-center text-sm text-gray-700 dark:text-gray-300"
                  >
                    {t("investors.noData")}
                  </td>
                </tr>
              ) : (
                paginatedData.map((investor) => (
                  <tr
                    key={investor.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {investor.code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium">
                          {getInitials(investor.name)}
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {investor.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-1 mb-1">
                          <IdCard className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                          <span>{investor.identifier}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-700 dark:text-gray-300">
                      {dayjs(investor.joinedDate).format("DD/MM/YYYY")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900 dark:text-white">
                      {(investor.totalSpend).toLocaleString('vi-VN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}{" "} VNĐ
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-700 dark:text-gray-300">
                      {investor.totalOrders}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {getStatusBadge(investor.status)}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary hover:text-white dark:text-primary-dark hover:bg-primary dark:hover:bg-primary-dark/20 rounded-md transition-colors"
                        onClick={() => openInvestorDetails(investor.id)}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {/* edit button */}
                      <button
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary hover:text-white dark:text-primary-dark hover:bg-primary dark:hover:bg-primary-dark/20 rounded-md transition-colors"
                        onClick={() => console.log("Edit investor:", investor.id)}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {/* lock */}
                      <button
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-primary/20 rounded-md transition-colors"
                        onClick={() => console.log("Lock investor:", investor.id)}
                      >
                        {investor.status === "Active" ? <Ban className="w-4 h-4" /> : <KeyRound className="w-4 h-4 text-primary" />}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Panigation
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default InvestorManagement;
